import type { ReactNode } from 'react';
import { isValidElement } from 'react';

import env from 'helpers/env';
import history from 'helpers/history';
import { sanitizeUrl } from 'helpers/url';

import * as Sentry from '@sentry/react';
import packageInfo from '../../../package.json';
import {
  buildApiFailureIdentity,
  getNormalizedApiEndpoint
} from './errorIdentity';

export interface IApiContext {
  url?: string;
  endpoint?: string;
  method?: string;
  status?: number | string;
  errorCode?: string | number;
  backendMessage?: string;
  responseBody?: unknown;
  responseHeaders?: unknown;
  requestId?: string;
  traceId?: string;
  correlationId?: string;
  graphQLOperationName?: string;
}

export interface IReportErrorToastOptions {
  presentation?: 'toast' | 'inline';
  title?: string;
  message?: ReactNode;
  variant?: string;
  error?: unknown;
  errorCode?: string | number;
  feature?: string;
  component?: string;
  page?: string;
  action?: string;
  api?: IApiContext;
  extraContext?: Record<string, unknown>;
  source?: string;
  alreadyCaptured?: boolean;
}

const ERROR_TOAST_DEDUPE_MS = 3000;
const MAX_RECENT_TOAST_KEYS = 1000;
const SENTRY_ENVIRONMENTS = ['int', 'prod', 'staging'];
const SENSITIVE_KEY_PATTERN =
  /(password|secret|token|cookie|session|jwt|authorization|auth|api[-_]?key|refresh[-_]?token|access[-_]?token)/i;
const recentToastReports = new Map<string, number>();
const URL_CONTEXT_KEYS = new Set(['url', 'endpoint', 'browserUrl']);

const getReleaseVersion = () => `frontend@${packageInfo.version}`;

const isSensitiveKey = (key: string) => SENSITIVE_KEY_PATTERN.test(key);

const sanitizeStringValue = (value: string, key?: string) => {
  if (key && isSensitiveKey(key)) {
    return '[REDACTED]';
  }

  if (/^Bearer\s+/i.test(value)) {
    return '[REDACTED]';
  }

  return value;
};

const shouldSanitizeUrlValue = (value: string, key?: string) =>
  /^https?:\/\//i.test(value) || Boolean(key && URL_CONTEXT_KEYS.has(key));

const sanitizeForSentry = (
  value: unknown,
  key?: string,
  depth = 0
): unknown => {
  if (depth > 5) {
    return '[Truncated]';
  }

  if (value == null) {
    return value;
  }

  if (typeof value === 'string') {
    const sanitizedValue = sanitizeStringValue(value, key);

    if (shouldSanitizeUrlValue(sanitizedValue, key)) {
      return sanitizeUrl(sanitizedValue);
    }

    return sanitizedValue;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeForSentry(value.message, key, depth + 1),
      stack: value.stack
    };
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeForSentry(item, key, depth + 1));
  }

  if (typeof Headers !== 'undefined' && value instanceof Headers) {
    return sanitizeForSentry(
      Object.fromEntries(value.entries()),
      key,
      depth + 1
    );
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((accumulator, [entryKey, entryValue]) => {
      accumulator[entryKey] = isSensitiveKey(entryKey)
        ? '[REDACTED]'
        : sanitizeForSentry(entryValue, entryKey, depth + 1);
      return accumulator;
    }, {});
  }

  return String(value);
};

const extractTextFromNode = (value: ReactNode): string | undefined => {
  if (value == null || typeof value === 'boolean') {
    return undefined;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const textValue = value
      .map(item => extractTextFromNode(item))
      .filter(Boolean)
      .join(' ')
      .trim();

    return textValue || undefined;
  }

  if (isValidElement(value)) {
    return extractTextFromNode(value.props?.children);
  }

  return undefined;
};

const normalizeError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error);
  }

  const fallbackError = new Error(fallbackMessage);

  if (error && typeof error === 'object') {
    Object.assign(fallbackError, error);
  }

  return fallbackError;
};

const getApiContextFromError = (error: Error): IApiContext | undefined => {
  const apiError = error as Error &
    Partial<IApiContext> & {
      data?: unknown;
      responseBody?: unknown;
      responseHeaders?: unknown;
    };
  const responseBody =
    apiError.responseBody ??
    apiError.data ??
    (typeof apiError.message === 'object' ? apiError.message : undefined);
  const responseBodyRecord =
    responseBody && typeof responseBody === 'object'
      ? (responseBody as Record<string, unknown>)
      : undefined;
  const inferredErrorCodeRaw =
    responseBodyRecord?.error_code ?? responseBodyRecord?.errorCode;
  const inferredErrorCode =
    typeof inferredErrorCodeRaw === 'string' ||
    typeof inferredErrorCodeRaw === 'number'
      ? inferredErrorCodeRaw
      : undefined;
  const inferredBackendMessageRaw =
    responseBodyRecord?.detail ?? responseBodyRecord?.message;
  const inferredBackendMessage =
    typeof inferredBackendMessageRaw === 'string'
      ? inferredBackendMessageRaw
      : undefined;

  const hasApiContext = [
    apiError.url,
    apiError.endpoint,
    apiError.method,
    apiError.status,
    apiError.errorCode,
    apiError.backendMessage,
    apiError.responseBody,
    apiError.responseHeaders,
    apiError.requestId,
    apiError.traceId,
    apiError.correlationId,
    apiError.graphQLOperationName,
    responseBody
  ].some(value => value !== undefined);

  if (!hasApiContext) {
    return undefined;
  }

  return {
    url: apiError.url,
    endpoint: apiError.endpoint,
    method: apiError.method,
    status: apiError.status,
    errorCode: apiError.errorCode ?? inferredErrorCode,
    backendMessage: apiError.backendMessage ?? inferredBackendMessage,
    responseBody,
    responseHeaders: apiError.responseHeaders,
    requestId: apiError.requestId,
    traceId: apiError.traceId,
    correlationId: apiError.correlationId,
    graphQLOperationName: apiError.graphQLOperationName
  };
};

export const resolveApiContext = ({
  api,
  error
}: Pick<IReportErrorToastOptions, 'api' | 'error'>): IApiContext | undefined =>
  api ?? getApiContextFromError(normalizeError(error, 'API Error'));

const getStatusString = (status?: number | string) =>
  status == null ? undefined : String(status);

const buildApiTag = (method?: string, endpoint?: string) => {
  if (!method && !endpoint) {
    return undefined;
  }

  return [method, endpoint].filter(Boolean).join(' ');
};

const markErrorAsCaptured = (error: Error) => {
  Object.defineProperty(error, '__avomaErrorToastCaptured', {
    value: true,
    configurable: true
  });
};

const hasErrorBeenCaptured = (error: unknown) =>
  error instanceof Error &&
  Boolean(
    (error as Error & { __avomaErrorToastCaptured?: boolean })
      .__avomaErrorToastCaptured
  );

const buildDedupeKey = ({
  title,
  message,
  api,
  errorCode,
  feature,
  source
}: {
  title?: string;
  message?: string;
  api?: IApiContext;
  errorCode?: string | number;
  feature?: string;
  source?: string;
}) => {
  const apiIdentity = buildApiFailureIdentity({ source, api, errorCode });

  if (apiIdentity) {
    return JSON.stringify({
      apiIdentity,
      concreteEndpoint: sanitizeUrl(
        api?.endpoint ?? api?.url ?? api?.graphQLOperationName ?? ''
      )
    });
  }

  return JSON.stringify({ title, message, feature, source });
};

const pruneRecentToastReports = (now: number) => {
  for (const [existingKey, timestamp] of recentToastReports) {
    if (now - timestamp >= ERROR_TOAST_DEDUPE_MS) {
      recentToastReports.delete(existingKey);
    }
  }
};

const hasRecentDuplicate = (key: string, now: number) => {
  pruneRecentToastReports(now);

  const lastCapturedAt = recentToastReports.get(key);

  if (lastCapturedAt && now - lastCapturedAt < ERROR_TOAST_DEDUPE_MS) {
    return true;
  }

  return false;
};

const markRecentReport = (key: string, now: number) => {
  recentToastReports.set(key, now);

  if (recentToastReports.size > MAX_RECENT_TOAST_KEYS) {
    const oldestKey = recentToastReports.keys().next().value;
    if (oldestKey) {
      recentToastReports.delete(oldestKey);
    }
  }
};

export const reportErrorToast = ({
  presentation = 'toast',
  title,
  message,
  variant = 'error',
  error,
  errorCode,
  feature,
  component,
  page,
  action,
  api,
  extraContext,
  source = 'errorNotification',
  alreadyCaptured = false
}: IReportErrorToastOptions) => {
  try {
    if (!SENTRY_ENVIRONMENTS.includes(env.name)) {
      return;
    }

    if (alreadyCaptured || hasErrorBeenCaptured(error)) {
      return;
    }

    const contextPrefix = presentation === 'inline' ? 'inline_error' : 'toast';
    const eventTitle =
      presentation === 'inline'
        ? 'Frontend Error Handled Inline'
        : 'Frontend Error Toast Displayed';
    const normalizedMessage =
      extractTextFromNode(message) || title || eventTitle;
    const exception = normalizeError(error, normalizedMessage);
    const effectiveApi = resolveApiContext({ api, error: exception });
    const dedupeKey = buildDedupeKey({
      title,
      message: normalizedMessage,
      api: effectiveApi,
      errorCode,
      feature,
      source
    });
    const now = Date.now();

    if (hasRecentDuplicate(dedupeKey, now)) {
      return;
    }

    const sanitizedApi = sanitizeForSentry(effectiveApi) as
      | IApiContext
      | undefined;
    const sanitizedContext = sanitizeForSentry(extraContext);
    const route = history.location?.pathname || window.location.pathname;
    const normalizedApiEndpoint = getNormalizedApiEndpoint(
      sanitizedApi?.endpoint ?? sanitizedApi?.url
    );
    const apiTag = buildApiTag(
      sanitizedApi?.method,
      normalizedApiEndpoint ?? sanitizedApi?.graphQLOperationName
    );
    const statusTag = getStatusString(sanitizedApi?.status);
    const normalizedErrorCode = sanitizedApi?.errorCode || errorCode;
    const apiFailureIdentity = buildApiFailureIdentity({
      source,
      api: sanitizedApi,
      errorCode: normalizedErrorCode
    });

    Sentry.withScope(scope => {
      scope.setLevel('error');
      if (apiFailureIdentity) {
        scope.setFingerprint([
          `frontend-api-error-${presentation}`,
          apiFailureIdentity
        ]);
      }
      scope.setTag(
        `${contextPrefix}_event`,
        presentation === 'inline'
          ? 'frontend_error_handled_inline'
          : 'frontend_error_toast_displayed'
      );
      scope.setTag(`${contextPrefix}_variant`, variant);
      scope.setTag('route', route);

      if (feature) {
        scope.setTag('feature', feature);
      }

      if (apiTag) {
        scope.setTag('api', apiTag);
      }

      if (statusTag) {
        scope.setTag('http_status', statusTag);
      }

      if (normalizedErrorCode) {
        scope.setTag('error_code', normalizedErrorCode);
      }

      scope.setContext(contextPrefix, {
        title,
        message: normalizedMessage,
        variant,
        source,
        errorCode: normalizedErrorCode
      });

      scope.setContext('ui', {
        route,
        page,
        component,
        action,
        browserUrl: sanitizeUrl(window.location.href)
      });

      scope.setContext('application', {
        environment: env.name,
        release: getReleaseVersion(),
        version: packageInfo.version,
        timestamp: new Date().toISOString()
      });

      scope.setContext('network', {
        online: navigator.onLine
      });

      scope.setContext(`${contextPrefix}_report`, {
        eventTitle
      });

      if (sanitizedApi) {
        scope.setContext('api_request', {
          method: sanitizedApi.method,
          url: sanitizedApi.url,
          endpoint: sanitizedApi.endpoint,
          requestId: sanitizedApi.requestId,
          traceId: sanitizedApi.traceId,
          correlationId: sanitizedApi.correlationId,
          graphQLOperationName: sanitizedApi.graphQLOperationName
        });

        scope.setContext('api_response', {
          status: sanitizedApi.status,
          backendMessage: sanitizedApi.backendMessage,
          errorCode: sanitizedApi.errorCode
        });

        if (sanitizedApi.responseBody !== undefined) {
          scope.setExtra('backend_payload', sanitizedApi.responseBody);
        }

        if (sanitizedApi.responseHeaders !== undefined) {
          scope.setExtra('response_headers', sanitizedApi.responseHeaders);
        }
      }

      scope.setExtra(
        `${contextPrefix}_payload`,
        sanitizeForSentry({ title, message: normalizedMessage })
      );

      if (sanitizedContext !== undefined) {
        scope.setExtra('extra_context', sanitizedContext);
      }

      Sentry.captureException(exception);
    });

    if (error instanceof Error) {
      markErrorAsCaptured(error);
    }

    markRecentReport(dedupeKey, now);
  } catch (reportingError) {
    console.error('Failed to report error toast to Sentry', reportingError);
  }
};
